import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';

import Icon from 'components/commons/Icon';
import dayjs from 'utils/dayjs';
import { useDesktopCalendarPicker } from 'hooks';

import {
  DEFAULT_CLIENT_DATE_FORMAT,
  DEFAULT_MOBILE_DATE_FORMAT,
  getDisplayValue as defaultGetDisplayValue,
  toNativeDateValue,
  parseDate,
} from 'utils';

import LoadableDesktopCalendarPicker from './loadableDesktopCalendarPicker';
import {
  Select,
  SelectLabel,
  InlineContainer,
  InlineText,
  HiddenDateInput,
  OutlineFieldWrap,
  OutlineField,
  OutlineFieldLabel,
  OutlineCalendarIcon,
  OutlineArrowIcon,
  OutlineDateInput,
} from './styles';

const DEFAULT_MIN_DATE = '2006-01-04';

const DatePicker = ({
  value,
  onChange,
  onAccept,
  intl,
  getDisplayValue,
  chart,
  type,
  disableFuture,
  minDate,
  maxDate,
  views,
  openTo,
  disableToolbar,
}) => {
  const isDesktopCalendarEnabled = useDesktopCalendarPicker();
  const formatDisplay = getDisplayValue || defaultGetDisplayValue;
  const nativeValue =
    toNativeDateValue(value) || dayjs().format(DEFAULT_MOBILE_DATE_FORMAT);
  const displayValue = formatDisplay(nativeValue, intl.locale);
  const nativeMaxDate = disableFuture
    ? dayjs().format(DEFAULT_MOBILE_DATE_FORMAT)
    : toNativeDateValue(maxDate);
  const nativeMinDate = toNativeDateValue(minDate) || DEFAULT_MIN_DATE;

  const handleNativeChange = event => {
    const nextDate = event.target.value
      ? parseDate(event.target.value) || dayjs()
      : dayjs();

    if (onChange) {
      onChange(nextDate);
    }

    if (onAccept) {
      onAccept(nextDate);
    }
  };

  const renderOutlineField = (label, trigger, fieldProps = {}) => (
    <OutlineField {...fieldProps}>
      <OutlineCalendarIcon name="calendar" />
      <OutlineFieldLabel>{label}</OutlineFieldLabel>
      <OutlineArrowIcon name="arrow" />
      {trigger}
    </OutlineField>
  );

  const handleTriggerKeyDown = useCallback((event, onToggle) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle();
    }
  }, []);

  const renderTrigger = useCallback(
    ({ isOpen, onToggle }) => {
      if (type === 'inline') {
        return (
          <InlineContainer
            chart
            onClick={onToggle}
            onKeyDown={event => handleTriggerKeyDown(event, onToggle)}
            role="button"
            tabIndex={0}
          >
            <InlineText>{displayValue}</InlineText>
            <Icon name="arrow" />
          </InlineContainer>
        );
      }

      if (type === 'outline') {
        return renderOutlineField(displayValue, null, {
          onClick: onToggle,
          onKeyDown: event => handleTriggerKeyDown(event, onToggle),
          role: 'button',
          tabIndex: 0,
        });
      }

      if (type === 'select') {
        return (
          <Select type="button" open={isOpen} onClick={onToggle}>
            <SelectLabel>{displayValue}</SelectLabel>
            <Icon name="arrow" />
          </Select>
        );
      }

      return (
        <Select type="button" chart={chart} open={isOpen} onClick={onToggle}>
          <SelectLabel>{displayValue}</SelectLabel>
        </Select>
      );
    },
    [chart, displayValue, handleTriggerKeyDown, type]
  );

  const nativeInput = (
    <HiddenDateInput
      type="date"
      name="currency-datepicker"
      pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
      value={nativeValue}
      min={nativeMinDate}
      max={nativeMaxDate}
      onChange={handleNativeChange}
    />
  );

  const outlineNativeInput = (
    <OutlineDateInput
      type="date"
      name="currency-datepicker"
      pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
      value={nativeValue}
      min={nativeMinDate}
      max={nativeMaxDate}
      aria-label={displayValue || nativeValue}
      onChange={handleNativeChange}
    />
  );

  const nativePicker = (() => {
    if (type === 'inline') {
      return (
        <InlineContainer>
          <InlineText>{displayValue}</InlineText>
          <Icon name="arrow" />
          {nativeInput}
        </InlineContainer>
      );
    }

    if (type === 'outline') {
      return renderOutlineField(displayValue, outlineNativeInput);
    }

    return (
      <Select as="div" chart={chart}>
        <SelectLabel>{displayValue}</SelectLabel>
        {nativeInput}
      </Select>
    );
  })();

  if (!isDesktopCalendarEnabled) {
    if (type === 'outline') {
      return <OutlineFieldWrap>{nativePicker}</OutlineFieldWrap>;
    }

    return nativePicker;
  }

  const desktopPicker = (
    <LoadableDesktopCalendarPicker
      fallback={nativePicker}
      locale={intl.locale}
      value={value || nativeValue}
      onChange={onChange}
      onAccept={onAccept}
      minDate={minDate || DEFAULT_MIN_DATE}
      maxDate={maxDate}
      disableFuture={disableFuture}
      fullWidth={type === 'outline'}
      renderTrigger={renderTrigger}
      views={views}
      openTo={openTo}
      disableToolbar={disableToolbar}
    />
  );

  if (type === 'outline') {
    return <OutlineFieldWrap>{desktopPicker}</OutlineFieldWrap>;
  }

  return desktopPicker;
};

DatePicker.propTypes = {
  getDisplayValue: PropTypes.func,
  value: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  onChange: PropTypes.func,
  onAccept: PropTypes.func,
  format: PropTypes.string,
  disableFuture: PropTypes.bool,
  autoOk: PropTypes.bool,
  variant: PropTypes.string,
  chart: PropTypes.bool,
  position: PropTypes.shape({
    horizontal: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf(['left', 'center', 'right']),
    ]),
    vertical: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.oneOf(['top', 'center', 'bottom']),
    ]),
  }),
  minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  intl: PropTypes.shape({
    locale: PropTypes.string,
  }).isRequired,
  type: PropTypes.string,
  views: PropTypes.arrayOf(PropTypes.oneOf(['year', 'month', 'date'])),
  openTo: PropTypes.oneOf(['year', 'month', 'date']),
  disableToolbar: PropTypes.bool,
};

DatePicker.defaultProps = {
  onChange: () => {},
  onAccept: () => {},
  format: DEFAULT_CLIENT_DATE_FORMAT,
  disableFuture: true,
  autoOk: true,
  variant: 'inline',
  chart: null,
  position: {
    horizontal: 310,
    vertical: 24,
  },
  minDate: DEFAULT_MIN_DATE,
  maxDate: new Date('2100-01-01'),
  value: undefined,
  type: undefined,
  getDisplayValue: null,
  views: undefined,
  openTo: undefined,
  disableToolbar: false,
};

export default injectIntl(DatePicker);
